import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Row, Col, Card, CardBody, Input, Pagination, PaginationItem, PaginationLink, Badge, Label } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { getCookie } from "../../Helpers/Utils";
import Swal from "sweetalert2";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const PurchaseList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const user = getCookie("user_data") ? JSON.parse(getCookie("user_data")) : {};
    const isAdmin = user?.role === 'admin';

    const [purchases, setPurchases] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 });

    const [filters, setFilters] = useState({
        farmer_name: "",
        from_date: null,
        to_date: null,
        page: 1,
        limit: 10
    });

    const loadPurchases = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                farmer_name: filters.farmer_name,
                page: filters.page,
                limit: filters.limit,
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };

            // Empty string params தேவையில்லன்னு remove பண்ணு
            Object.keys(params).forEach(k => params[k] === "" && delete params[k]);

            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/purchase/list.php?${queryParams}` });
            if (res?.success) {
                setPurchases(res.data);
                setAnalytics(res.analytics);
                setPagination({
                    current_page: res.current_page,
                    total_pages: res.total_pages,
                    total_records: res.total_records
                });
            } else {
                toast.error(res?.message || "Failed to fetch data");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => { loadPurchases(); }, [filters.page, filters.farmer_name]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
    };

    const handleImportCSV = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/purchase/import_csv.php",
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (res?.success) {
                Swal.fire({
                    title: "Import Successful",
                    html: `
                        <div class="text-start">
                            <p>Total Rows: <b>${res.data.total_rows}</b></p>
                            <p class="text-success">Successful: <b>${res.data.success}</b></p>
                            <p class="text-danger">Failed: <b>${res.data.failed}</b></p>
                        </div>
                    `,
                    icon: "success"
                });
                loadPurchases();
            } else {
                toast.error(res?.message || "Import failed");
            }
        } catch (err) {
            toast.error("Error uploading file");
        } finally {
            dispatch(loadingFalse());
            fileInputRef.current.value = "";
        }
    };

    const viewReceipt = async (id) => {
        dispatch(loadingTrue());
        try {
            const token = getCookie("token");
            // const response = await fetch(`http://localhost/cotton-mill/purchase/download.php?id=${id}`, {
            const response = await fetch(`https://muthaiah-cotton.qads.co.in/cotton-mill/purchase/receipt.php?id=${id}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const blob = await response.blob();
            const fileURL = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = fileURL;
            link.setAttribute('download', `receipt_${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(fileURL);
        } catch (err) {
            toast.error("Failed to download receipt.");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold text-theme m-0">Purchase History</h3>
                        <p className="text-muted small m-0">Track and manage raw cotton intake</p>
                    </div>
                    <div className="d-flex gap-2">
                        {isAdmin && (
                            <>
                                <input
                                    type="file"
                                    accept=".csv"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={handleImportCSV}
                                />
                                <Button color="white" className="rounded-pill px-3 shadow-sm border" onClick={() => fileInputRef.current.click()}>
                                    <i className="bx bx-upload me-1"></i> Import CSV
                                </Button>
                            </>
                        )}
                        <Button color="success" className="rounded-pill px-4 shadow-sm" onClick={() => navigate("/purchases/add")}>
                            <i className="bx bx-plus me-1"></i> New Purchase
                        </Button>
                    </div>
                </div>

                {analytics && (
                    <Row className="mb-4 g-3 text-uppercase">
                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-4 text-center">
                                <CardBody className="p-3">
                                    <p className="text-muted x-small fw-bold mb-1">Total Purchase</p>
                                    <h4 className="fw-bold mb-0 text-primary">₹{analytics.total_purchase_amount.toLocaleString('en-IN')}</h4>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-4 text-center">
                                <CardBody className="p-3">
                                    <p className="text-muted x-small fw-bold mb-1">Paid Amount</p>
                                    <h4 className="fw-bold mb-0 text-success">₹{analytics.total_paid.toLocaleString('en-IN')}</h4>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-4 text-center">
                                <CardBody className="p-3">
                                    <p className="text-muted x-small fw-bold mb-1">Total Balance</p>
                                    <h4 className="fw-bold mb-0 text-danger">₹{analytics.total_balance.toLocaleString('en-IN')}</h4>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-3">
                        <Row className="g-3 align-items-end">
                            <Col md={4}>
                                <Label className="small fw-bold">Farmer / Vendor Name</Label>
                                <Input style={{ height: '45px' }} name="farmer_name" placeholder="Search name..." value={filters.farmer_name} onChange={handleFilterChange} />
                            </Col>
                            <Col md={3}>
                                <Label className="small fw-bold">From Date</Label>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    disableFuture
                                    value={filters.from_date}
                                    onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={3}>
                                <Label className="small fw-bold">To Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.to_date}
                                    onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                    minDate={filters.from_date}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={2}>
                                <Button color="dark" block style={{ height: '45px' }} className="fw-bold" onClick={loadPurchases}>
                                    <i className="bx bx-filter me-1"></i> Filter
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4">No</th>
                                    <th>Purchase ID / Date</th>
                                    <th>Farmer & Product</th>
                                    <th className="text-center">Net Weight</th>
                                    <th className="text-end">Amount</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {purchases.length > 0 ? purchases.map((item, index) => (
                                    <tr key={item.purchase_id}>
                                        <td className="ps-4">
                                            <span className="text-muted">{((filters.page - 1) * filters.limit) + index + 1}</span>
                                        </td>
                                        <td>
                                            <div className="fw-bold text-dark">{item.purchase_no}</div>
                                            <small className="text-muted">{dayjs(item.purchase_date).format('DD MMM YYYY')}</small>
                                        </td>
                                        <td>
                                            <div className="fw-bold">{item.farmer.name}</div>
                                            <Badge color="light" className="text-primary border">{item.product.name}</Badge>
                                        </td>
                                        <td className="text-center">
                                            <div className="fw-bold">{item.quantity.net_kg} Kg</div>
                                            <div className="x-small text-muted">Tare: {item.quantity.total_kg - item.quantity.net_kg} Kg</div>
                                        </td>
                                        <td className="text-end">
                                            <div className="small text-muted">@ ₹{item.pricing.rate}</div>
                                            <div className="fw-bold text-dark">₹{item.pricing.total_with_gst?.toLocaleString('en-IN')}</div>
                                        </td>
                                        <td className="text-center">
                                            <Badge color={item.payment_summary.status === 'paid' ? 'success' : 'warning'} pill className="px-3">
                                                {item.payment_summary.status?.toUpperCase()}
                                            </Badge>
                                            {item.payment_summary.balance_amount > 0 && (
                                                <div className="text-danger x-small mt-1 fw-bold">Bal: ₹{item.payment_summary.balance_amount?.toLocaleString('en-IN')}</div>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-1">
                                                {isAdmin && (
                                                    <Button color="light" size="sm" className="border shadow-sm text-primary" onClick={() => navigate(`/purchases/${item.purchase_id}`)} title="Edit">
                                                        <i className="bx bx-edit-alt"></i>
                                                    </Button>
                                                )}
                                                <Button color="light" size="sm" className="border shadow-sm text-danger" onClick={() => viewReceipt(item.purchase_id)} title="PDF Receipt">
                                                    <i className="bx bxs-file-pdf"></i>
                                                </Button>
                                                <Button color="light" size="sm" className="border shadow-sm text-dark" onClick={() => navigate(`/purchases-view/${item.purchase_id}`)} title="View">
                                                    <i className="bx bx-show"></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="7" className="text-center py-5 text-muted">No purchase records found matching filters.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded" aria-label="Page navigation">
                            {/* Previous Button */}
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>

                            {/* Dynamic Page Numbers */}
                            {[...Array(pagination?.total_pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                // Logic: Show first, last, and pages around the current page
                                if (
                                    pageNumber === 1 ||
                                    pageNumber === pagination?.total_pages ||
                                    (pageNumber >= filters.page - 1 && pageNumber <= filters.page + 1)
                                ) {
                                    return (
                                        <PaginationItem active={pageNumber === filters.page} key={i}>
                                            <PaginationLink onClick={() => setFilters({ ...filters, page: pageNumber })}>
                                                {pageNumber}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (pageNumber === filters.page - 2 || pageNumber === filters.page + 2) {
                                    // Add ellipsis (...)
                                    return (
                                        <PaginationItem disabled key={i}>
                                            <PaginationLink>...</PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            {/* Next Button */}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default PurchaseList;